import { AvatarImage } from '@radix-ui/react-avatar';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/deprecated/OldTable';
import { Avatar } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';
import { fetchUserMatchHistory } from '@/features/users/api/getMatchHistory';
import { formatMapName } from '@/utils/format';
import { formatDateTime } from '@/utils/formatDateTime';

import { fetchMatchHistory } from '../api/getMatchHistory';

interface MatchHistoryProps {
  teamId?: string;
  userId?: string;
}
const MatchHistory: React.FC<MatchHistoryProps> = ({ teamId, userId }) => {
  const [limit, setLimit] = useState<number>(10);
  const { ref, inView } = useInView({ threshold: 0.8 });
  const navigate = useNavigate();
  const { data: maps, isFetching } = useQuery({
    queryKey: ['matchHistory', teamId || userId, limit], // Use teamId or userId based on which one is provided
    queryFn: () => {
      if (teamId) {
        return fetchMatchHistory(teamId, limit);
      } else if (userId) {
        return fetchUserMatchHistory(userId, limit);
      }
      return Promise.resolve(null);
    },
    placeholderData: keepPreviousData,
  });

  const formatDateAndTime = (date: Date) => {
    const formatted = formatDateTime(date);
    return (
      <>
        {formatted.timeWithZone}
        <br />
        {formatted.formattedDayAndMonth}
      </>
    );
  };

  const handleRowClick = (matchId: number) => {
    //length 9 being Scrim
    console.log(matchId.toString().length);
    if (matchId.toString().length == 9) {
      navigate(`/scrim/history/${matchId}`);
    } else {
      //other lengths being league matches
      navigate(`/match/${matchId}`);
    }
  };

  useEffect(() => {
    if (inView) {
      setLimit((limit) => (limit += 5));
    }
  }, [inView]);

  if (!maps) return null;

  return (
    <div className="inline-grid w-full flex-col overflow-hidden pb-24 lg:flex">
      <div className="overflow-x-auto">
        <div className="mb-3 text-lg font-semibold">Match History</div>
        <TableWrapper className="rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-2/12">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Date
                  </span>
                </TableHead>
                <TableHead className="w-4/12">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Opponent
                  </span>
                </TableHead>
                <TableHead>
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Team win
                  </span>
                </TableHead>
                <TableHead className="text-center">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Score
                  </span>
                </TableHead>
                <TableHead>
                  <span className="uppercase tracking-wide text-muted-foreground">
                    map
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {maps.map((row, index) => (
                <TableRow
                  key={index}
                  className="cursor-pointer transition-colors"
                  title="Go to match"
                  onClick={() => handleRowClick(row.matchid)}
                  ref={index == limit - 1 ? ref : null}
                >
                  <TableCell>{formatDateAndTime(row.endTime)}</TableCell>
                  <TableCell className="flex items-center gap-x-2">
                    <Avatar>
                      <AvatarImage
                        src={`/images/uploads/${row.opponentImage}`}
                        alt={`${row.opponent}`}
                      />
                    </Avatar>
                    <div className="font-medium ">{row.opponent}</div>
                  </TableCell>
                  <TableCell
                    className={`font-medium uppercase ${
                      row.result == 'Win' ? '!text-[#0BEA21]' : '!text-red'
                    }`}
                  >
                    {row.result}
                  </TableCell>
                  <TableCell className="px-0 text-center font-medium ">
                    <>
                      <div className={`mr-0.5 inline-block w-7 text-center`}>
                        {row.team1_score}
                      </div>
                      :
                      <div className={`ml-0.5 inline-block w-7 text-center`}>
                        {row.team2_score}
                      </div>
                    </>
                  </TableCell>
                  <TableCell className="font-medium ">
                    <div className="w-12">
                      <img
                        src={`/images/${row.mapname}.jpg`}
                        className="rounded-lg border border-border"
                        title={formatMapName(row.mapname)}
                        alt=""
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
        {isFetching && (
          <div className="relative h-20">
            <Spinner size="sm" className="mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
};

export { MatchHistory };
